import apiUtils from '../api/submissionApiUtils';
import { authenticatedFetch } from '@forwardfinancing/component-library';
import { documentTagKeyFor } from '../constants/stipUploadTags';
import * as types from './actionTypes';
import {
  clearSubmissionInfoLoading
} from './submissionInfoActions';

export function confirmSubmissionAttachments(info) {
  return {type: types.CONFIRM_SUBMISSION_ATTACHMENTS, status: info.status};
}

export function setFilesToBeUploaded(files) {
  return {type: types.SET_FILES_TO_BE_UPLOADED, files};
}

export function setFailedFiles(file) {
  return {type: types.SET_FAILED_FILES, file};
}

export function clearSubmissionAttachments() {
  return {type: types.CLEAR_SUBMISSION_ATTACHMENTS};
}

export function postNewSubmissionAttachments(file, id) {
  const filename = apiUtils.formatFilenameForAttachments(file.name, file.tag);
  const url = apiUtils.newSubmissionPresignedURLEndpoint(id, filename);
  const body = { filename: file.name, lead_id: id };
  return dispatch => postAttachments({url, file, body})(dispatch);
}

export function postRenewalSubmissionAttachments(file, id) {
  const url = apiUtils.renewalPresignedURLEndpoint(file.name);
  const body = { filename: file.name, uuid: id };
  return dispatch => postAttachments({url, file, body})(dispatch);
}

export function postStipSubmissionAttachments(file, id) {
  const filename = apiUtils.formatFilenameForStips(file.name, file.tag);
  const url = apiUtils.stipPresignedURLEndpoint(id, filename);
  // Underwriting stores `attachments.document_tags` as symbol keys (validated
  // against `Attachment::DOCUMENT_TAGS.keys`). The portal tracks tags as
  // labels internally (compared against `field.display`, shown in the UI), so
  // we convert label -> key just at the wire. Unmapped labels (e.g. custom
  // stip names returned by underwriting we don't have in the dictionary yet)
  // fall through unchanged rather than getting dropped.
  const tag = documentTagKeyFor(file.tag);
  // Underwriting requires a description when the tag is "other"; the tagger UI
  // collects it on `file.description` for that case.
  // owner_uuid / expires_on are required by underwriting for a Drivers License.
  // They ride this body (which reaches POST /v1/attachment), not the
  // presigned-URL request, which carries its data in the query string.
  const body = {
    filename: file.name,
    stip_opportunity_uuid: id,
    tag,
    description: file.description,
    owner_uuid: file.owner_uuid,
    expires_on: file.expires_on
  };
  return dispatch => postAttachments({url, file, body})(dispatch);
}

function postAttachments({url, file, body}){
  return dispatch => {
    return authenticatedFetch(url, {
        method: 'POST',
        body: body,
        headers: {
          'Content-Type': 'application/octet-stream'
        }
      })
      .then(response => {
        if(!response.ok){
          throw(new UploadError("Failed to fetch presigned URL", file));
        }
        return parsePresignedURL(response);
      })
      .then(presignedURL => {
        return uploadToS3(presignedURL, file, body);
      })
      .then(info => {
        return dispatch(confirmSubmissionAttachments(info));
      })
      .catch(error => {
        dispatch(clearSubmissionInfoLoading());
        dispatch(setFailedFiles(file && file.name));
        const { message } = error;
        throw(new UploadError(message || error, file));
      });
  };
}

function UploadError(message, file) {
  this.name = "UploadError";
  this.message = message;
  this.file = file;
  this.stack = (new Error()).stack;
}

export function uploadToS3(presignedURL, file, body) {
  return fetch(presignedURL, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': ''
    }
  }).then(response => {
    if (!response.ok) {
      throw(new UploadError("Failed to Upload to S3", file));
    }
    const { bucket, key } = parseS3BucketKey(presignedURL);
    return postPresignedAttachmentUrl(bucket, key, body);
   })
    .then(response => processResponse(response));
}


function postPresignedAttachmentUrl(bucket, key, body_params){
  return authenticatedFetch(apiUtils.attachmentSubmissionUrl(), {
      method: 'POST',
      body: JSON.stringify({ ...body_params, 'bucket': bucket, 'key': key }),
      headers: {
        'Content-Type': 'application/json'
      }
    }
  );
}

function parsePresignedURL(response){
  return response.json().then(({presigned_url}) => presigned_url);
}

function parseS3BucketKey(s3Url) {
  const url = new URL(s3Url);
  const pathComponents = url.pathname.split('/');
  const bucket = pathComponents[1];
  const key = pathComponents.slice(2).join('/');
  return {bucket, key};
}

function processResponse(response) {
  return response.json().then(json => {
    return { data: json, status: response.status };
  });
}
